export type AssetConditionType = 'New' | 'Used';
export type AssetConditionValue = { type: AssetConditionType , name: string} | null;

// Tier 1/2 may only be set to "New" when the asset is from the current or prior year.
const TIER_12_NEW_MAX_AGE_YEARS = 1;
// TreasuryAssetCondition treats an asset as "New" when it is at most 2 years old (current + 2 prior years).
const TREASURY_NEW_MAX_AGE_YEARS = 2;

const parseYear = (assetYear: number | string | null | undefined): number | null => {
  const year = typeof assetYear === 'string' ? parseInt(assetYear, 10) : assetYear;
  return (year && !isNaN(year)) ? year : null;
};

/**
 * Whether "New" may be selected for the given tier/year.
 * Tier 1/2 restrict "New" to current + prior year assets; this restriction is bypassed when
 * `allowOverride` is set (the pricing tab). Tier 3/4 and unknown tiers always allow "New".
 */
export const isNewAssetConditionAllowed = (assetTier: number | null, assetYear: number | null, allowOverride = false): boolean => {
  if (allowOverride) return true;
  if (assetTier !== 1 && assetTier !== 2) return true;
  const year = parseYear(assetYear);
  if (year === null) return true; // cannot restrict without a year
  return (new Date().getFullYear() - year) <= TIER_12_NEW_MAX_AGE_YEARS;
};

/**
 * Tier-based asset condition options. Labels are always "New"/"Used" (no age suffix). For tier 1/2
 * the "New" option is only offered for current + prior year assets unless overridden (pricing tab).
 */
export const getAssetConditionOptions = (assetTier: number | null, assetYear: number | null = null, allowOverride = false): Exclude<AssetConditionValue, null>[] => {
  const options: Exclude<AssetConditionValue, null>[] = [
    {type: 'New', name: 'New'},
    {type: 'Used', name: 'Used'},
  ];
  return isNewAssetConditionAllowed(assetTier, assetYear, allowOverride)
    ? options
    : options.filter(o => o.type !== 'New');
};

/**
 * Derived Treasury asset condition: "New" when the asset is at most 2 years old (2024/25/26 in 2026),
 * "Used" otherwise. Applies across all asset tiers and is independent of the (overrideable) AssetCondition.
 */
export const getTreasuryAssetCondition = (assetYear: number | string | null | undefined): AssetConditionType => {
  const year = parseYear(assetYear);
  if (year === null) return 'Used';
  return (new Date().getFullYear() - year) <= TREASURY_NEW_MAX_AGE_YEARS ? 'New' : 'Used';
};

// Legacy options for backward compatibility
export const AssetConditionValueOptions: Exclude<AssetConditionValue, null>[] = [
  {type: 'New', name: 'New / Demo' },
  {type: 'Used', name: 'Used'},
];
